import React, { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import MorphingGradient from '../components/MorphingGradient';
import AmbientBackground from '../components/AmbientBackground';
import AnimatedIcon from '../components/AnimatedIcon';
import { motion } from 'framer-motion';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: '',
    honeypot: '', // Anti-spam honeypot field
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot
    if (formData.honeypot) {
      setStatus({ type: 'error', message: 'Spam detected.' });
      return;
    }

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setStatus({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Connect to backend API
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          organization: formData.organization || null,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }

      const data = await response.json();
      
      setStatus({
        type: 'success',
        message: 'Thank you! Your message has been sent successfully. We\'ll get back to you shortly.',
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        organization: '',
        message: '',
        honeypot: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({
        type: 'error',
        message: error.message || 'Something went wrong. Please try again or email us directly at info@yamai.tech',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative pt-32 pb-24">
      <SEO 
        title={t('contact.title')}
        description="Contact Yamai for partnerships, clinical trials, or inquiries about our medical AI projects. Based in Breda, Netherlands. Email: info@yamai.tech"
        keywords="contact Yamai, medical AI partnerships, clinical trials, healthcare collaboration, Netherlands"
      />

      {/* Professional Background */}
      <MorphingGradient />
      <AmbientBackground />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-gradient-animate">
              {t('contact.title')}
            </span>
          </h1>
          <p className="text-[#8A90A0] text-xl max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Contact Information</h2>
              <p className="text-[#8A90A0] leading-relaxed mb-8">
                Whether you're a healthcare professional, researcher, investor, or simply curious about our work,
                we'd love to hear from you.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4 p-4 glass-effect rounded-xl border border-yamai-purple-500/30 hover-glow-purple">
                <div className="w-12 h-12 flex-shrink-0">
                  <AnimatedIcon icon={Mail} color="#9C27B0" size={24} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Email</h3>
                  <a
                    href="mailto:info@yamai.tech"
                    className="text-yamai-cyan-400 hover:text-yamai-cyan-300 transition-colors"
                  >
                    info@yamai.tech
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 glass-effect rounded-xl border border-yamai-cyan-500/30 hover-glow-cyan">
                <div className="w-12 h-12 flex-shrink-0">
                  <AnimatedIcon icon={MapPin} color="#00BCD4" size={24} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Location</h3>
                  <p className="text-[#8A90A0]">
                    Breda, Noord-Brabant<br />
                    The Netherlands
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Info Box */}
            <div className="glass-effect rounded-2xl p-6 border border-yamai-purple-500/30 hover-glow-purple">
              <h3 className="text-white font-semibold mb-3">Partnership Opportunities</h3>
              <p className="text-[#8A90A0] text-sm leading-relaxed">
                We're actively seeking partnerships with hospitals, research institutions, and healthcare technology companies.
                If you're interested in collaboration or clinical validation, please reach out.
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-effect rounded-2xl p-8 border border-[#2B3544]"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-white mb-2 block">
                  Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="glass-effect border-[#2B3544] text-white placeholder:text-[#8A90A0] focus:border-yamai-cyan-500"
                  placeholder="Your full name"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-white mb-2 block">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="glass-effect border-[#2B3544] text-white placeholder:text-[#8A90A0] focus:border-yamai-cyan-500"
                  placeholder="your.email@example.com"
                />
              </div>

              {/* Organization (Optional) */}
              <div>
                <Label htmlFor="organization" className="text-white mb-2 block">
                  Organization (Optional)
                </Label>
                <Input
                  id="organization"
                  name="organization"
                  type="text"
                  value={formData.organization}
                  onChange={handleChange}
                  className="glass-effect border-[#2B3544] text-white placeholder:text-[#8A90A0] focus:border-yamai-cyan-500"
                  placeholder="Your company or institution"
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-white mb-2 block">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="glass-effect border-[#2B3544] text-white placeholder:text-[#8A90A0] focus:border-yamai-cyan-500 resize-none"
                  placeholder="Tell us about your inquiry..."
                />
              </div>

              {/* Honeypot (hidden) */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />

              {/* Status Message */}
              {status.message && (
                <div
                  className={`flex items-start gap-3 p-4 rounded-lg ${
                    status.type === 'success'
                      ? 'bg-[#10B981]/20 border border-[#10B981]/30'
                      : 'bg-red-500/20 border border-red-500/30'
                  }`}
                >
                  {status.type === 'success' ? (
                    <CheckCircle size={20} className="text-[#10B981] flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <p
                    className={`text-sm ${
                      status.type === 'success' ? 'text-[#10B981]' : 'text-red-400'
                    }`}
                  >
                    {status.message}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-yamai-cyan-500 to-yamai-purple-500 hover:opacity-90 text-white py-6 rounded-xl transition-all duration-300 hover:scale-105 hover-glow-cyan disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    Send Message
                    <Send size={18} className="ml-2" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;